import { groupBy } from 'lodash';
import {
  mapSecGroupToOption,
  SecGroupSection,
} from '~components/popups-modals/RightsModal/Tabs/tabs.util';
import { DbFolder, DbSecurityGroup } from '~src/types/db';
import { SEC_GROUP_PRIORITY } from '~src/types/enums';

/** `DbSecurityGroup['color']` map;
 * Should use this for tags color if present
 */
export const secGroupColorMap = {
  1: '#0a324d',
  2: '#008000',
  3: '#FF8C00',
  4: '#8B0000',
  5: '#8A2BE2',
  11: '#1F5FA6',
  21: '#4cd964',
  31: '#ffcc00',
  41: '#ff4141',
  51: '#bf7aff',
};

export const secGroupSectionsConfig = {
  [SEC_GROUP_PRIORITY.WORKING_GROUPS]: {
    intlId: `sec-group-wg`,
    color: '#FFCA2E',
  },
  [SEC_GROUP_PRIORITY.INTERNAL]: {
    intlId: `sec-group-internal`,
    color: '#2CBD9B',
  },
  [SEC_GROUP_PRIORITY.RESERVED]: {
    intlId: `sec-group-reserved1`,
    color: '#3C435C',
  },
  [SEC_GROUP_PRIORITY.RESERVED_2]: {
    intlId: `sec-group-reserved2`,
    color: '#3C435C',
  },
  [SEC_GROUP_PRIORITY.CUSTOM]: {
    intlId: `sec-group-custom`,
    color: '#3A8FFF',
  },
};

/** Map DbSecurityGroup[] to Select.OptGroup > Select.Option list  */
export const mapSecGroupsToSections = (secGroups: DbSecurityGroup[] = []) => {
  const groupedByPriority = groupBy(secGroups, 'priority');

  const sections: SecGroupSection[] = Object.entries(groupedByPriority).map(
    ([key, groups]) => {
      return {
        key: `sec-group-section-${key}`,
        label: `Section ${key}`,
        intlId:
          secGroupSectionsConfig[key]?.intlId ||
          secGroupSectionsConfig[SEC_GROUP_PRIORITY.CUSTOM].intlId,
        color: secGroupSectionsConfig[key]?.color,
        options: groups.map(mapSecGroupToOption),
      };
    }
  );

  /** 
     * The higher value, the higher in the dropdown it should be.
      Eg.
      (2) - internal Perm grouping SG are second to bottom.
      (1) - WG_SG are in the bottom. 
    */
  return sections.reverse();
};

/**
 * transforms db folder list in directory tree
 */
export const arrToTree = (
  arr: DbFolder[],
  parentId: DbFolder['parent_id']
): TableEntry[] => {
  return arr
    .filter((dbFolder) => dbFolder.parent_id === parentId)
    .map((dbFolder) => {
      const children = arrToTree(arr, dbFolder.id);

      return {
        ...dbFolderToTreeObj(dbFolder),
        children: children.length ? children : undefined,
      };
    });
};

export interface TableEntry {
  /** String(folder.id) */
  key: string;
  parentId: string;
  title: string;
  children?: TableEntry[];
  dbFolder: DbFolder;
}

const dbFolderToTreeObj = (dbFolder: DbFolder): TableEntry => {
  const { id, name, parent_id } = dbFolder;

  return {
    key: String(id),
    parentId: String(parent_id),
    title: name || 'Unknown',
    children: [],
    dbFolder,
  };
};
